import { useCallback } from 'react';
import { VoiceAIActivity } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

const hasMatchingVoiceAiActivity = (activities: VoiceAIActivity[], candidate: VoiceAIActivity) => {
  return activities.some(
    (activity) => activity.outcome === candidate.outcome && activity.createdAt === candidate.createdAt
  );
};

export const useThreadAiStateWSHandler = () => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { isCallRecordItem } = SMSDataSharedUtils.threadItemsUtils;

  const { selectedLocationIds } = useAppScopeStore();
  const { isActive: fusionEnabled } = useMessagingAIRCustomizationFlag(selectedLocationIds);
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.THREAD_AI_STATE>>(
    async (event) => {
      const { params } = event;
      const locationId = params.threadAiActivity.locationId ?? '';
      const threadAiActivity = params.threadAiActivity;

      const weaveNumber = threadAiActivity.locationPhoneNumber;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      if (fusionEnabled) {
        await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: threadAiActivity.threadId ?? '',
            locationId,
          },
          updater: (thread) => {
            return thread
              ? {
                  ...thread,
                  aiStatus: {
                    ...thread.aiStatus,
                    status: threadAiActivity.status,
                    statusDetails: threadAiActivity.details,
                    isActive: threadAiActivity.isActive,
                    isEscalated: threadAiActivity.isEscalated,
                    isSeeded: threadAiActivity.isSeeded,
                    isBotDeactivatedByPhoneConfig: threadAiActivity.isBotDeactivatedByPhoneConfig,
                    escalationType: threadAiActivity.escalationType,
                  },
                }
              : undefined;
          },
          isOptimistic: false,
        });

        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId: threadAiActivity.threadId ?? '',
            locationId,
            threadItemId: '',
          },
          updater: () => {
            return {
              aiActivity: {
                ...threadAiActivity,
              },
            };
          },
          isOptimistic: false,
        });
      }
      const voiceAiActivity = params.threadAiActivity.voiceAiActivity;

      if (voiceAiActivity?.relatedThreadCallId && voiceAiActivity.threadId && voiceAiActivity.locationId) {
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId: voiceAiActivity.threadId,
            locationId: voiceAiActivity.locationId,
            threadItemId: voiceAiActivity.relatedThreadCallId,
          },
          updater: (existingItem) => {
            if (!existingItem || !isCallRecordItem(existingItem)) return existingItem;

            const existingActivities = existingItem.callRecord.voiceAiActivities ?? [];

            if (hasMatchingVoiceAiActivity(existingActivities, voiceAiActivity)) {
              return existingItem;
            }

            return {
              ...existingItem,
              callRecord: {
                ...existingItem.callRecord,
                voiceAiActivities: [...existingActivities, voiceAiActivity],
              },
            };
          },
          isOptimistic: false,
          updateOnly: true,
        });
      }
    },
    [
      checkUserLocationPhoneAccess,
      fusionEnabled,
      unifiedThreadUpdater.upsertUnifiedThread,
      unifiedThreadUpdater.upsertThreadItem,
    ]
  );

  return wsEventHandler;
};

export const useThreadAiStateWSSubscription = () => {
  const handler = useThreadAiStateWSHandler();
  return useWebsocketEventSubscription(NotificationType.THREAD_AI_STATE, handler);
};
